import React from 'react';
import {Modal} from "../common/Modal";
import {DateUtility} from "../common/DateUtility";
import {SubscriptionDataUtility} from "../common/SubscriptionDataUtility";
import {PillButton} from "../common/PillButton";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {SubscriptionInvoiceLinesDetail} from "./SubscriptionInvoiceLinesDetail";
import {InvoiceDataUtility} from "../common/InvoiceDataUtility";
import {CollapsibleInvoiceFpDataDisplay} from "../invoices/CollapsibleInvoiceFpDataDisplay";
import _ from "lodash";
import {Subscription} from "./SubscriptionTypes";
import {ExternalStripeInvoice} from "../invoices/InvoiceTypes";

interface SubscriptionDetailProps {
    cancel: Function;
    subscription?: Subscription;
    invoiceDefaultsUrl: string | undefined;
    onClickSync: React.MouseEventHandler<HTMLDivElement>;
    onClickInvoice: Function;
    onClickCancelSubscription: React.MouseEventHandler<HTMLDivElement>;
}

export let SubscriptionDetail = function (props: SubscriptionDetailProps) {
    const subscription = (_.clone(props.subscription) || {}) as Subscription;
    const fpInvoiceDataAnchor = `invoice-fp-data-${subscription.fp_invoice?.invoicenumber}`;

    let invoiceDefaultsLinkDisplay = (
        <a
            href={props.invoiceDefaultsUrl}
            className="external-text-link"
            target="_blank"
        >
            here
            <img
                className="external-link-icon"
                src="/static/images/open-external-link-moss-green.svg"
            />
        </a>
    )

    let durationDisplay = DateUtility.dbDateToLocalDisplayDate(subscription.start_date) + ' - ';
    if (subscription.end_date) {
        durationDisplay += DateUtility.dbDateToLocalDisplayDate(subscription.end_date)
    } else {
        durationDisplay += 'Forever'
    }
    if (subscription.subscription_billing_period?.name) {
        durationDisplay += ` (billed ${subscription.subscription_billing_period.name.toLowerCase()})`
    }

    let subscriptionLinks = null;
    if (subscription.ext_stripe_subscription_schedule_url) {
        let subscriptionLinkDisplay = null;
        let subscriptionLinkDisclaimer = null;
        let scheduleLabel = null;
        let outerWrapperClass = "subscription-links ";
        let scheduleWrapperClass = "subscription-schedule ";

        if (subscription.ext_stripe_subscription_url) {
            outerWrapperClass += 'with-subscription'
            scheduleWrapperClass += 'with-subscription'
            scheduleLabel = 'Schedule'
            subscriptionLinkDisplay = (
                <a
                    href={subscription.ext_stripe_subscription_url}
                    className="subscription-link external-text-link external-link-box"
                    target="_blank"
                >
                    <span className="link-text">
                        Subscription
                        <img
                            className="external-link-icon"
                            src="/static/images/open-external-link-moss-green.svg"
                        />
                    </span>
                </a>
            )
        } else {
            scheduleLabel = 'Schedule'
            subscriptionLinkDisclaimer = (
                <div className="subscription-disclaimer">
                    <span className="subscription-not-found">
                        Subscription won't exist until its start date.
                    </span>
                </div>
            )
        }

        subscriptionLinks = (
            <div className={outerWrapperClass}>
                {subscriptionLinkDisplay}
                <span className={scheduleWrapperClass}>
                    <a
                        href={subscription.ext_stripe_subscription_schedule_url}
                        className="schedule-link external-text-link external-link-box"
                        target="_blank"
                    >
                        <span className="link-text">
                            {scheduleLabel}
                            <img
                                className="external-link-icon"
                                src="/static/images/open-external-link-moss-green.svg"
                            />
                        </span>
                    </a>
                </span>
                {subscriptionLinkDisclaimer}
            </div>
        )
    }

    let totalDisplay = SubscriptionDataUtility.getSubscriptionTotalDisplay(subscription)
    let linesDisplay = SubscriptionDataUtility.getSubscriptionLinesDisplay(subscription)

    let needInvoiceSync = false;
    let syncStatusMessage = 'No subscription invoices need to be synced.'
    if (subscription.unsynced_ext_stripe_subscription_invoices?.length > 0) {
        needInvoiceSync = true;
        syncStatusMessage = `Found ${subscription.unsynced_ext_stripe_subscription_invoices.length} unsynced invoice`
        if (subscription.unsynced_ext_stripe_subscription_invoices.length > 1) {
            syncStatusMessage += 's';
        }
        syncStatusMessage += '.';
    }

    let invoiceListRows = [];
    if (subscription.draft_ext_stripe_subscription_invoices) {
        for (let a = 0; a < subscription.draft_ext_stripe_subscription_invoices.length; a++) {
            let extStripeDraftInvoice: ExternalStripeInvoice = subscription.draft_ext_stripe_subscription_invoices[a];
            invoiceListRows.push(
                <a
                    key={'d-' + a}
                    id={`subscriptionDetailInvoiceLine-${extStripeDraftInvoice.id}`}
                    className="invoice-list-row body-row"
                    href={extStripeDraftInvoice.ext_stripe_invoice_url}
                    target="_blank"
                >
                    <div className="invoice-list-row-content">
                        <div className="invoice-list-row-cell label">
                            <span className="main-label">Draft invoice</span>
                            <span className="sublabel">(Stripe only - can be synced after grace period ends)</span>
                        </div>
                        <div className="invoice-list-row-cell billing-status">
                            <div className="invoice-status-content">
                                <div className="invoice-status-badge draft">DRAFT</div>
                            </div>
                        </div>
                        <div className="invoice-list-row-cell chevron">
                            <img
                                className="external-link-icon external-link-box-header-icon"
                                src="/static/images/open-external-link-textcolor.svg"
                            />
                        </div>
                    </div>
                </a>
            )
        }
    }

    if (subscription.invoices) {
        for (let z = 0; z < subscription.invoices.length; z++) {
            let invoice = subscription.invoices[z];
            let invoiceStatusClass = InvoiceDataUtility.invoiceStatusToClass(invoice.invoice_status)
            let invoiceTitle = SubscriptionDataUtility.getSubscriptionInvoiceTitle(subscription, invoice);
            invoiceListRows.push(
                <div
                    key={z}
                    id={`subscriptionDetailInvoiceLine-${invoice.id}`}
                    className="invoice-list-row body-row"
                    onClick={() => props.onClickInvoice(invoice.id)}
                >
                    <div className="invoice-list-row-content">
                        <div className="invoice-list-row-cell label">
                            {invoiceTitle}
                        </div>
                        <div className="invoice-list-row-cell billing-status">
                            <div className="invoice-status-content">
                                <div className={`invoice-status-badge ${invoiceStatusClass}`}>
                                    {invoice.invoice_status?.name}
                                </div>
                                <div className="invoice-status-additional-info">
                                    {invoice.invoice_status?.additional_info}
                                </div>
                            </div>
                        </div>
                        <div className="invoice-list-row-cell chevron">
                            <img
                                src="/static/images/chevron-right-textcolor.svg"
                                className="list-row-chevron-icon"
                            />
                        </div>
                    </div>
                </div>
            )
        }
    }

    let modalBoxClasses = "subscription-modal-box ";
    let headerMainText = 'Subscription';
    if (!subscription.active) {
        headerMainText += ' (Canceled)'
        modalBoxClasses += ' canceled'
    }

    return (
        <Modal
            headerTextContentRenderer={() => (
                <div className={"subscription-detail-header " + (subscription.fp_invoice ? 'from-fp-invoice' : '')}>
                    <div className="common-modal-header-main">
                        <div className="common-modal-header-main-content">
                            <div className="common-modal-header-main-label">
                                {headerMainText}
                            </div>
                            <ConditionalRenderer if={subscription.fp_invoice} renderer={() => (
                                <a
                                    href={`#${fpInvoiceDataAnchor}`}
                                    className="common-modal-header-main-sub-label subscription-fp-invoice-link"
                                >{`(Created from FP invoice ${subscription.fp_invoice?.invoicenumber})`}</a>
                            )}/>
                        </div>
                    </div>
                    <div className="subscription-detail-header-basics">
                        <div className="subscription-detail-header-total">{totalDisplay}</div>
                        <div className="subscription-detail-header-lines">{linesDisplay}</div>
                    </div>
                </div>
            )}
            additionalOptionsRenderer={() => (
                <PillButton
                    onClick={props.onClickCancelSubscription}
                    buttonText="CANCEL SUBSCRIPTION"
                    containerClasses="cancel-button-container"
                    buttonClasses="cancel-button"
                />
            )}
            onClickCloseOption={props.cancel}
            boxClass={modalBoxClasses}
        >
            <div className="common-form subscription-form subscription-detail">
                <div className="common-form-body">
                    <div className="form-section subscription-top-details">
                        <div className="subscription-top-details-content">
                            <div className="subscription-top-details-column left">
                                <div className="subscription-top-details-column-content">
                                    <div className="test-subscription-id">{subscription.id}</div>
                                    <div className="subscription-detail stripe-customer">
                                        <div className="common-form-field">
                                            <span className="common-form-field-label">Stripe Customer</span>
                                            <span className="common-form-field-input-container">
                                                {subscription.ext_stripe_customer?.name}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="subscription-detail qbo-customer">
                                        <div className="common-form-field">
                                            <span className="common-form-field-label">QBO Customer</span>
                                            <span className="common-form-field-input-container">
                                                {subscription.ext_qbo_customer?.DisplayName}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="subscription-detail duration">
                                        <div className="common-form-field">
                                            <span className="common-form-field-label">Duration</span>
                                            <span className="common-form-field-input-container">
                                                {durationDisplay}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="subscription-detail stripe-links">
                                        <div className="common-form-field">
                                            <span className="common-form-field-label">Stripe Links</span>
                                            <span className="common-form-field-input-container">
                                                {subscriptionLinks}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="subscription-top-details-column right">
                                <div className="subscription-top-details-content">
                                    <div className="common-form-field vertical">
                                        <div className="common-form-field-label">
                                            Description
                                        </div>
                                        <div className="common-form-field-input-container">
                                            <div className="subscription-description-display">
                                                {subscription.description || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-section subscription-detail-invoice-list">
                        <div className="form-section-label">
                            <div className="form-section-label-content">
                                <div className="form-section-label-text">
                                    Invoices
                                </div>
                                <div className="form-section-label-details">
                                    <div className="sync-status">
                                        <div className={`sync-status-message ${needInvoiceSync ? 'warning' : ''}`}>
                                            {syncStatusMessage}
                                        </div>
                                        <ConditionalRenderer if={needInvoiceSync} renderer={() => (
                                            <PillButton
                                                onClick={props.onClickSync}
                                                containerClasses="sync-button-container subscription-sync-button-container"
                                                buttonClasses="sync-button subscription-sync-button"
                                                leftIconSrc={"/static/images/sync-white.svg"}
                                                buttonText="SYNC"
                                            />
                                        )}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="form-section-body">
                            {(() => {
                                if (invoiceListRows.length > 0) {
                                    return (
                                        <div className="common-form-list">
                                            <div className="common-form-list-header">
                                                <div
                                                    className="common-form-list-header-content invoice-list-row header-row">
                                                    <div className="invoice-list-row-content">
                                                        <div
                                                            className="common-form-list-row-cell invoice-list-row-cell label">
                                                            Name
                                                        </div>
                                                        <div
                                                            className="common-form-list-row-cell invoice-list-row-cell billing-status"
                                                        >
                                                            Status
                                                        </div>
                                                        <div
                                                            className="common-form-list-row-cell invoice-list-row-cell chevron"
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="common-form-list-body">
                                                {invoiceListRows}
                                            </div>
                                        </div>
                                    )
                                } else {
                                    return (
                                        <div className="empty-subscription-invoice-list-message">
                                            No synced invoices found.
                                        </div>
                                    )
                                }
                            })()}
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section-label">Subscription invoice details</div>
                        <div className="form-section-body">
                            <SubscriptionInvoiceLinesDetail
                                subscriptionInvoiceLines={subscription.subscription_invoice_lines}
                            />

                            <div className="common-form-body-row">
                                <div className="invoice-defaults-blurb">
                                    See invoice default memo and footer settings {invoiceDefaultsLinkDisplay}.
                                </div>
                            </div>
                        </div>
                    </div>

                    <ConditionalRenderer if={subscription.fp_invoice} renderer={() => (
                        <CollapsibleInvoiceFpDataDisplay
                            fpInvoiceDataAnchor={fpInvoiceDataAnchor}
                            fpInvoice={subscription.fp_invoice}
                        />
                    )}/>
                </div>
            </div>
        </Modal>
    );
};
